/**
 * Property Key Selector Component
 * 
 * A reusable component for selecting property keys with the ability to:
 * - Select from existing property keys
 * - Add new property keys that don't exist yet
 * - Validate property key format
 */

import React, { useEffect, useState, useMemo } from 'react';
import { Select, AutoComplete, Spin } from 'antd';
import { GlobalPropertiesService } from '../../services/api/globalPropertiesService';
import { PropertyDefinitionResponse } from '../../types/globalProperties';

const { Option } = Select;

interface PropertyKeySelectorProps {
  value?: string | string[];
  onChange?: (_value: string | string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  mode?: 'tags' | 'multiple' | undefined;
  style?: React.CSSProperties;
  className?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  /** Filter properties by their property_type (e.g., 'date', 'list', 'string') */
  filterByType?: string | string[];
}

// In-memory cache for property definitions (stores full definitions for filtering)
interface CachedProperty {
  property_key: string;
  property_type?: string;
}
let propertyDefinitionsCache: CachedProperty[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const PropertyKeySelector: React.FC<PropertyKeySelectorProps> = ({
  value,
  onChange,
  placeholder = 'Select or type property key',
  disabled = false,
  allowClear = true,
  mode,
  style,
  className,
  onBlur,
  onFocus,
  filterByType
}) => {
  const [propertyDefinitions, setPropertyDefinitions] = useState<CachedProperty[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load property definitions from API
  useEffect(() => {
    const loadPropertyDefinitions = async () => {
      // Check cache first
      const now = Date.now();
      if (propertyDefinitionsCache && (now - cacheTimestamp) < CACHE_DURATION) {
        setPropertyDefinitions(propertyDefinitionsCache);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const response = await GlobalPropertiesService.listPropertyDefinitions({
          skip: 0,
          limit: 1000 // Get properties (backend may have max limit)
        });
        
        // Extract property_key and property_type from response
        const definitions: CachedProperty[] = response.properties.map((p: any) => ({
          property_key: 'property_key' in p ? p.property_key : (p.definition?.property_key || p.property_key),
          property_type: 'property_type' in p ? p.property_type : (p.definition?.property_type || p.property_type)
        })).filter((d: CachedProperty) => d.property_key);
        
        // Update cache
        propertyDefinitionsCache = definitions;
        cacheTimestamp = now;
        
        setPropertyDefinitions(definitions);
      } catch (err: any) {
        console.error('[PropertyKeySelector] Error loading property definitions:', err);
        setError(err.message || 'Failed to load property keys');
        // On error, still allow manual entry (tags mode will work)
        setPropertyDefinitions([]);
      } finally {
        setLoading(false);
      }
    };

    void loadPropertyDefinitions();
  }, []);

  // Filter property definitions based on filterByType prop
  const filteredDefinitions = React.useMemo(() => {
    if (!filterByType) return propertyDefinitions;
    
    const allowedTypes = Array.isArray(filterByType) ? filterByType : [filterByType];
    return propertyDefinitions.filter(def => 
      def.property_type && allowedTypes.includes(def.property_type)
    );
  }, [propertyDefinitions, filterByType]);

  // Extract just the property keys for the dropdown
  const propertyKeys = React.useMemo(() => 
    filteredDefinitions.map(def => def.property_key),
    [filteredDefinitions]
  );

  // Filter function for searching
  const filterOption = useMemo(() => {
    return (input: string, option?: { label: string; value: string }) => {
      if (!input) return true;
      const searchTerm = input.toLowerCase();
      const optionValue = option?.value?.toLowerCase() || '';
      const optionLabel = option?.label?.toLowerCase() || '';
      return optionValue.includes(searchTerm) || optionLabel.includes(searchTerm);
    };
  }, []);

  // Validate property key format
  const validatePropertyKey = (key: string): boolean => {
    if (!key) return true; // Empty is valid (will be handled by required validation)
    // Property key must be lowercase alphanumeric with underscores
    return /^[a-z0-9_]+$/.test(key);
  };

  // Handle tag creation (when user types a new value)
  const _handleTagCreation = (inputValue: string): string | false => {
    const trimmed = inputValue.trim();
    if (!trimmed) return false;
    
    // Validate format
    if (!validatePropertyKey(trimmed)) {
      return false; // Invalid format, prevent creation
    }
    
    return trimmed;
  };

  // Handle change with validation
  const handleChange = (newValue: string | string[]) => {
    if (mode === 'tags' || mode === 'multiple') {
      // For tags/multiple mode, validate each value
      const values = Array.isArray(newValue) ? newValue : [newValue];
      const validValues = values.filter(v => !v || validatePropertyKey(v));
      
      if (validValues.length !== values.length) {
        // Some values were invalid, don't update
        return;
      }
      
      onChange?.(validValues);
    } else {
      // Single value mode
      if (typeof newValue === 'string' && newValue && !validatePropertyKey(newValue)) {
        // Invalid format, don't update
        return;
      }
      onChange?.(newValue);
    }
  };

  // For single value mode, use AutoComplete to allow typing new values
  // For tags/multiple mode, use Select with tags
  if (!mode || mode === undefined) {
    // Single value mode - use AutoComplete
    const options = propertyKeys.map(key => ({ value: key, label: key }));
    
    return (
      <AutoComplete
        value={typeof value === 'string' ? value : undefined}
        onChange={(newValue) => {
          // onChange is called when user types or selects
          // Allow any value to be typed, validation happens in handleChange
          if (typeof newValue === 'string') {
            handleChange(newValue);
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        allowClear={allowClear}
        style={style}
        className={className}
        onBlur={(_e) => {
          // Validate on blur
          const currentValue = typeof value === 'string' ? value : '';
          if (currentValue && !validatePropertyKey(currentValue)) {
            // Invalid format - could show error message here
            // For now, just call onBlur callback
          }
          onBlur?.();
        }}
        onFocus={onFocus}
        options={options}
        filterOption={(inputValue, option) => {
          if (!inputValue) return true;
          const searchTerm = inputValue.toLowerCase();
          const optionValue = option?.value?.toLowerCase() || '';
          return optionValue.includes(searchTerm);
        }}
        notFoundContent={loading ? <Spin size="small" /> : (error ? 'Error loading properties' : 'No properties found')}
        onSelect={(selectedValue) => {
          // When user selects from dropdown
          handleChange(selectedValue);
        }}
      />
    );
  }

  // Tags or multiple mode - use Select
  return (
    <Select
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      allowClear={allowClear}
      mode={mode}
      style={style}
      className={className}
      onBlur={onBlur}
      onFocus={onFocus}
      showSearch
      filterOption={filterOption}
      loading={loading}
      notFoundContent={loading ? <Spin size="small" /> : (error ? 'Error loading properties' : 'No properties found')}
      tokenSeparators={[',']}
      tagRender={(props) => {
        const { label, value, closable, onClose } = props;
        const isValid = validatePropertyKey(value as string);
        return (
          <span
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              margin: '2px',
              backgroundColor: isValid ? '#f0f0f0' : '#fff2e8',
              border: `1px solid ${isValid ? '#d9d9d9' : '#ffbb96'}`,
              borderRadius: '4px',
              cursor: closable ? 'pointer' : 'default'
            }}
            onClick={closable ? onClose : undefined}
          >
            {label}
          </span>
        );
      }}
    >
      {propertyKeys.map(key => (
        <Option key={key} value={key} label={key}>
          {key}
        </Option>
      ))}
    </Select>
  );
};

